/**
 * MarkdownMessage — renders AI response text as formatted markdown.
 * Supports headings, bold, italic, tables, bullet/ordered lists, inline code, code blocks.
 *
 * `enableTableExport` (optional): when true, every rendered table gets a
 * small "Export table" (CSV/Excel) dropdown right below it — used by the
 * Open Analysis chat (see components/analysis/AnalysisTool.jsx).
 */
import { useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import TableExportMenu from "./TableExportMenu";

function TableWithOptionalExport({ children, enableTableExport }) {
  const containerRef = useRef(null);
  return (
    <div className="my-2">
      <div ref={containerRef} className="overflow-x-auto rounded border border-slate-200">
        <table className="w-full text-xs border-collapse">{children}</table>
      </div>
      {enableTableExport && <TableExportMenu tableRef={containerRef} />}
    </div>
  );
}

const baseComponents = {
  // Headings
  h1: ({ children }) => (
    <h1 className="text-base font-bold text-slate-800 mt-3 mb-1 leading-snug">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-bold text-slate-800 mt-3 mb-1 leading-snug">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-slate-700 mt-2.5 mb-1 leading-snug">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-slate-600 mt-2 mb-0.5">{children}</h4>
  ),

  // Paragraphs — no extra margin so spacing comes from the wrapping space-y
  p: ({ children }) => (
    <p className="text-sm leading-relaxed text-inherit mb-1.5 last:mb-0">{children}</p>
  ),

  // Bold / italic
  strong: ({ children }) => (
    <strong className="font-semibold text-inherit">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic text-inherit">{children}</em>
  ),

  // Lists
  ul: ({ children }) => (
    <ul className="list-disc list-inside text-sm space-y-0.5 pl-2 my-1.5">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-inside text-sm space-y-0.5 pl-2 my-1.5">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-sm leading-relaxed">{children}</li>
  ),

  // Horizontal rule
  hr: () => <hr className="border-slate-200 my-2" />,

  // Inline code
  code: ({ inline, children, ...props }) =>
    inline ? (
      <code
        className="bg-slate-100 text-slate-800 text-xs font-mono px-1.5 py-0.5 rounded"
        {...props}
      >
        {children}
      </code>
    ) : (
      <pre className="bg-slate-900 text-slate-100 text-xs font-mono p-3 rounded-md overflow-x-auto my-2 leading-relaxed">
        <code {...props}>{children}</code>
      </pre>
    ),

  // Tables (GFM) — the `table` entry itself is overridden per-render below
  // (see MarkdownMessage) so it can conditionally show the export menu.
  thead: ({ children }) => (
    <thead className="bg-slate-100 text-slate-700 font-semibold">{children}</thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr className="border-b border-slate-100 last:border-0 even:bg-slate-50/50">{children}</tr>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 text-left font-semibold text-slate-700">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-slate-600">{children}</td>
  ),

  // Blockquote
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-primary/40 pl-3 my-2 text-slate-500 italic">
      {children}
    </blockquote>
  ),

  // Links
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 hover:text-primary-hover"
    >
      {children}
    </a>
  ),
};

export default function MarkdownMessage({ content, className = "", enableTableExport = false }) {
  const components = {
    ...baseComponents,
    table: (props) => <TableWithOptionalExport {...props} enableTableExport={enableTableExport} />,
  };
  return (
    <div className={`markdown-message ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content || ""}
      </ReactMarkdown>
    </div>
  );
}
